import React, { useEffect, useState, useMemo } from 'react'
import * as R from 'ramda'
import { Dropdown, Form } from 'semantic-ui-react'
import './styles.css'

/**
 *
 * @param {object} props
 * @param {string} props.defaultValue
 * @param {string} props.iconColor
 * @param {function} props.onChange
 * @returns
 */
export default function FeedbackWidgetIconDropdown({
  'data-property': dataProperty,
  defaultValue,
  onChange,
  iconColor,
}) {
  const [selectedIcon, setSelectedIcon] = useState<IconOption>(null)

  function onSelect(data) {
    setSelectedIcon(data)
    onChange(data.value)
  }
  const singleIconOptions = useMemo(() => getSingleIconOptions(iconColor), [iconColor])
  const multipleIconOptions = useMemo(() => getMultipleIconOptions(iconColor), [iconColor])
  useEffect(() => {
    if (defaultValue) {
      const options = [
        ...hiddenOption,
        ...singleIconOptions,
        ...multipleIconOptions,
        ...iconSetOptions,
      ]
      const defaultIcon = R.find(R.propEq('value', defaultValue))(options)
      setSelectedIcon(defaultIcon)
    }
  }, [defaultValue, singleIconOptions, setSelectedIcon, multipleIconOptions])

  if (!selectedIcon) {
    return <Form.Select fluid options={[]} />
  }

  return (
    <Form.Field>
      <Dropdown
        data-property={dataProperty}
        selection
        text={<CustomDropdownText selectedIcon={selectedIcon} iconColor={iconColor} />}
      >
        <Dropdown.Menu>
          {hiddenOption.map((option) => (
            <CustomDropdownItem
              key={option.value}
              option={option}
              onSelect={onSelect}
              selectedIconValue={selectedIcon?.value}
            />
          ))}

          <Dropdown.Header content="Single icon" className="!my-2 !normal-case !text-base" />
          <div className="px-4 flex bg-white">
            {singleIconOptions.map((option) => (
              <CustomDropdownItem
                key={option.value}
                option={option}
                onSelect={onSelect}
                selectedIconValue={selectedIcon?.value}
              />
            ))}
          </div>
          <Dropdown.Header content="Multiple icons" className="!my-2 !normal-case !text-base" />
          <div className="px-4 flex flex-wrap bg-white pb-2">
            {multipleIconOptions.map((option) => (
              <CustomDropdownItem
                key={option.value}
                option={option}
                onSelect={onSelect}
                selectedIconValue={selectedIcon?.value}
              />
            ))}
          </div>

          <Dropdown.Header content="Icon set" className="!my-2 !normal-case !text-base" />
          <div className="px-4 flex flex-wrap bg-white pb-2">
            {iconSetOptions.map((option) => (
              <CustomDropdownItem
                key={option.value}
                option={option}
                onSelect={onSelect}
                selectedIconValue={selectedIcon?.value}
              />
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </Form.Field>
  )
}

// scrolling menu transition visible
type IconOption = {
  text: string | React.ReactNode
  value: string
}
type DropdownItemPropType = {
  option: IconOption
  selectedIconValue: string
  onSelect: Function
}
function CustomDropdownItem({ option, onSelect, selectedIconValue }: DropdownItemPropType) {
  return (
    <Dropdown.Item
      className="px-2 py-1 text-xl cursor-pointer hover:bg-grey-lighter rounded-md"
      key={option.value}
      text={option.text}
      value={option.value}
      onClick={() => onSelect(option)}
      active={selectedIconValue === option.value}
      selected={selectedIconValue === option.value}
    />
  )
}

type DropdownTextPropType = {
  selectedIcon: IconOption
  iconColor: string
}
function CustomDropdownText({ selectedIcon, iconColor }: DropdownTextPropType) {
  if (selectedIcon.value === 'single_quote_icons') {
    return (
      <div className="flex items-center">
        <i className="mr-2 fas fa-quote-left" style={{ color: iconColor }} /> Single quote
      </div>
    )
  }

  if (selectedIcon.value === 'single_quote_duotone_icons') {
    return (
      <div className="flex items-center">
        <i className="mr-2 fad fa-quote-left" style={{ color: iconColor }} /> Single quote
      </div>
    )
  }

  if (selectedIcon.value === 'single_quote_light_icons') {
    return (
      <div className="flex items-center">
        <i className="mr-2 fal fa-quote-left" style={{ color: iconColor }} /> Single quote
      </div>
    )
  }

  if (selectedIcon.value === 'single_quote_regular_icons') {
    return (
      <div className="flex items-center">
        <i className="mr-2 far fa-quote-left" style={{ color: iconColor }} /> Single quote
      </div>
    )
  }

  if (selectedIcon.value === 'rating_stars_icons') {
    return (
      <div className="flex items-center">
        <i className="mr-2 fas fa-star" style={{ color: iconColor }} /> Rating stars
      </div>
    )
  }

  if (selectedIcon.value === 'rating_stars_emojis') {
    return (
      <div className="flex items-center">
        <span className="mr-2">⭐</span> Rating stars
      </div>
    )
  }

  if (selectedIcon.value === 'happy_face_emojis') {
    return (
      <div className="flex items-center">
        <span className="mr-2">😀</span> Happy emoji party
      </div>
    )
  }
  if (selectedIcon.value === 'hand_emojis') {
    return (
      <div className="flex items-center">
        <span className="mr-2">👋</span> Hand emoji
      </div>
    )
  }
  if (selectedIcon.value === 'heart_emojis') {
    return (
      <div className="flex items-center">
        <span className="mr-2">💕</span> Heart emoji
      </div>
    )
  }

  if (selectedIcon.value === 'hidden') {
    return (
      <span>
        <i className="icon fas fa-eye-slash" />
        <span className="ml-1">Hide</span>
      </span>
    )
  }

  return null
}

const hiddenOption = [
  {
    value: 'hidden',
    text: (
      <span>
        <i className="icon fas fa-eye-slash" />
        <span className="ml-1">Hide</span>
      </span>
    ),
  },
]

function getSingleIconOptions(iconColor: string) {
  return [
    {
      value: 'single_quote_icons',
      text: <i className="fas fa-quote-left" style={{ color: iconColor }} />,
    },
    {
      value: 'single_quote_duotone_icons',
      text: <i className="fad fa-quote-left" style={{ color: iconColor }} />,
    },
    {
      value: 'single_quote_light_icons',
      text: <i className="fal fa-quote-left" style={{ color: iconColor }} />,
    },
    {
      value: 'single_quote_regular_icons',
      text: <i className="far fa-quote-left" style={{ color: iconColor }} />,
    },
  ]
}

function getMultipleIconOptions(iconColor: string) {
  return [
    {
      value: 'rating_stars_icons',
      text: <MultipleStarsIcon iconColor={iconColor} />,
    },
    {
      value: 'rating_stars_emojis',
      text: <EmojiText emojis={['⭐', '⭐', '⭐', '⭐', '⭐']} />,
    },
  ]
}

const iconSetOptions = [
  {
    value: 'happy_face_emojis',
    text: (
      <EmojiText
        emojis={[
          '😀',
          '😃',
          '😄',
          '😁',
          '😆',
          '😊',
          '😇',
          '🙂',
          '😉',
          '😌',
          '😍',
          '😘',
          '😙',
          '😚',
          '😋',
          '😛',
          '😝',
          '😜',
          '😎',
          '🤗',
        ]}
      />
    ),
  },
  {
    value: 'hand_emojis',
    text: (
      <EmojiText
        emojis={['👋', '🤚', '🖐', '👌', '✌️', '🤘', '👇', '👍', '👏', '🙌', '🤝', '💪']}
      />
    ),
  },
  {
    value: 'heart_emojis',
    text: <EmojiText emojis={['💕', '💞', '💓', '💗', '💖', '💘', '💝']} />,
  },
]

function EmojiText({ emojis }) {
  return emojis.map((emoji, index) => (
    <span key={index} className="mr-2">
      {emoji}
    </span>
  ))
}

function MultipleStarsIcon({ iconColor }) {
  return (
    <p className="ss-feedback-widget-stars">
      {Array(5)
        .fill()
        .map((_, index) => (
          <span key={index} className="mr-1">
            <i className="fas fa-star" style={{ color: iconColor }} />
          </span>
        ))}
    </p>
  )
}



// WEBPACK FOOTER //
// ./src/components/FeedbackWidgetIconDropdown/index.tsx